import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { LayerGroup, LayersControl, MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import { ZoneOverlays } from '../../../../util/zoneOverlays';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { getIndexList, getMyIndexList } from '../actions';
import './index.css';
import { alignLng, unwrapLines, WorldCopies } from '../../../../util/worldCopies';

// Fix for default marker icons in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const createCustomIcon = (vessels, color, indexName) => {
  const lines = vessels.map((vesselIndex, index) =>
    `<tspan x="10" dy="${index === 0 ? 0 : 14}">${vesselIndex}</tspan>`
  ).join("");

  const markersvg = `
    <svg
      width="50"
      height="50"
      viewBox="0 0 60 81"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M30.0336 80.2488C29.0354 80.2488 28.124 79.8148 27.473 79.0336C21.0496 71.0043 0 43.7918 0 29.9902C0 13.4544 13.4544 0 29.9902 0C46.5261 0 59.9804 13.4544 59.9804 29.9902C59.9804 43.8352 38.9743 71.0477 32.5075 79.0336C31.9433 79.8148 31.0318 80.2488 30.0336 80.2488ZM30.0336 6.72718C17.2303 6.72718 6.81398 17.1435 6.81398 29.9468C6.81398 42.7502 17.2303 53.1664 30.0336 53.1664C42.837 53.1664 53.2533 42.7502 53.2533 29.9468C53.2533 17.1435 42.837 6.72718 30.0336 6.72718Z"
        fill="${color}"
      />
      <circle cx="30" cy="30" r="19" fill="white"/>
      <text
        x="12"
        y="24"
        fill="black"
        fontWeight="600"
        textAnchor="middle"
        style="font-size: 12px;"
      >
        ${lines}
      </text>
    </svg>`;

  return L.divIcon({
    className: `custom-marker-${indexName}`,
    html: markersvg,
    iconSize: [70, 70],
    iconAnchor: [25, 50],
  });
};

function MapViewController({ bounds, center }) {
  const map = useMap();
  const prevBoundsRef = useRef(null);

  useEffect(() => {
    if (!map) return;
    if (
      bounds &&
      bounds.length === 2 &&
      Array.isArray(bounds[0]) &&
      bounds[0].length === 2 &&
      Array.isArray(bounds[1]) &&
      bounds[1].length === 2 &&
      (bounds[0][0] !== bounds[1][0] || bounds[0][1] !== bounds[1][1])
    ) {
      const boundsStr = JSON.stringify(bounds);
      if (prevBoundsRef.current !== boundsStr) {
        prevBoundsRef.current = boundsStr;
        try {
          map.fitBounds(bounds, { padding: [40, 40], maxZoom: 6 });
        } catch (e) {
          // ignore fitBounds calculation error
        }
      }
    } else if (center && Array.isArray(center) && center.length === 2 && (center[0] !== 0 || center[1] !== 0)) {
      map.setView(center, 3);
    }
  }, [bounds, center, map]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (map) map.invalidateSize();
    }, 200);
    return () => clearTimeout(timer);
  }, [map]);

  return null;
}

const VesselRouteMap = ({ vesselIndexs = '' }) => {
  const { indexId } = useParams()
  const isOwnerOnly = window.location.search.includes("myIndex=true");
  const [vesselIndexDetails, setVesselIndexDetails] = useState([])
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [hideSideBar, setHideSidebar] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  // Tile layer definitions
  const tileLayers = {
    worldStreet: {
      name: "World Street Map",
      url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
      attribution: "Esri"
    },
    seaMark: {
      name: "Sea Mark",
      url: "http://t1.openseamap.org/seamark/{z}/{x}/{y}.png",
      attribution: "OpenSeaMap"
    },
    osm: {
      name: "OpenStreetMap",
      url: "http://{s}.tile.osm.org/{z}/{x}/{y}.png",
      attribution: "OpenStreetMap contributors"
    },
    worldImagery: {
      name: "World Imagery",
      url: "http://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      attribution: "Esri"
    },
    worldGrayCanvas: {
      name: "World Gray Canvas",
      url: "http://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
      attribution: "Esri"
    },
    blackAndWhite: {
      name: "Black and White",
      url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png",
      attribution: "OpenStreetMap contributors"
    }
  };

  // 1. Initial data fetch: fetch all vessel indexes only once on mount
  useEffect(() => {
    let isCancelled = false;
    const fetchVesselData = async () => {
      try {
        setLoading(true);
        let response;
        if (isOwnerOnly) {
          response = await getMyIndexList();
        } else {
          response = await getIndexList();
        }

        if (isCancelled) return;

        if (!response?.data) {
          throw new Error("No data received");
        }

        // Process vesselIndex data to filter ports and mark validity
        const processedData = response?.data?.map((item) => {
          const validPorts = item?.port?.filter(
            (port) => port?.lat !== undefined && port?.lng !== undefined
          ) || [];
          return {
            ...item,
            port: validPorts,
            hasValidPorts: validPorts.length > 0,
          };
        });

        setVesselIndexDetails(processedData);
        setError(null);
      } catch (err) {
        if (!isCancelled) {
          setError("Failed to fetch Index data");
          console.error(err);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchVesselData();
    return () => {
      isCancelled = true;
    };
  }, [isOwnerOnly]);

  // 2. Synchronize selection in-memory without refetching or unmounting map
  useEffect(() => {
    if (!vesselIndexDetails || vesselIndexDetails.length === 0) return;

    const indexIdsArray = indexId
      ? indexId.split(",")
      : vesselIndexs !== undefined
        ? vesselIndexs ? vesselIndexs.split(",").filter(Boolean) : []
        : [];

    if (indexIdsArray.length > 0) {
      const matchingVessels = vesselIndexDetails.filter((item) =>
        indexIdsArray.includes(item._id)
      );
      setSelectedOptions(matchingVessels.map((vessel) => vessel.indexName));
    } else if (vesselIndexs === undefined && vesselIndexDetails[0]) {
      setSelectedOptions([vesselIndexDetails[0].indexName]);
    } else {
      setSelectedOptions([]);
    }
  }, [indexId, vesselIndexs, vesselIndexDetails]);

  const combinedPortData = useMemo(() => {
    if (!vesselIndexDetails || !selectedOptions || selectedOptions.length === 0) {
      return [];
    }

    const selectedIndex = vesselIndexDetails.filter(vesselIndex =>
      selectedOptions.some(option =>
        vesselIndex.indexName.toLowerCase() === option.toLowerCase()
      )
    );

    return selectedIndex.flatMap(vesselIndex =>
      vesselIndex.port.map(port => ({
        ...port,
        indexName: vesselIndex.indexName,
      }))
    );
  }, [vesselIndexDetails, selectedOptions]);

  const getMapBounds = () => {
    if (!combinedPortData || combinedPortData.length === 0) return [[0, 0]];

    const portCoordinates = combinedPortData.map(port => [port.lat, port.lng]);

    if (portCoordinates.length === 1) return portCoordinates;

    const latitudes = portCoordinates.map(coord => coord[0]);
    const longitudes = portCoordinates.map(coord => coord[1]);

    const minLat = Math.min(...latitudes);
    const maxLat = Math.max(...latitudes);
    const minLng = Math.min(...longitudes);
    const maxLng = Math.max(...longitudes);

    return [[minLat, minLng], [maxLat, maxLng]];
  };

  const getCenterPosition = () => {
    const bounds = getMapBounds();
    if (!bounds || bounds.length === 0 || (bounds.length === 1 && bounds[0][0] === 0 && bounds[0][1] === 0)) {
      return [20, 0];
    }
    if (bounds.length === 1) return bounds[0];

    const [[minLat, minLng], [maxLat, maxLng]] = bounds;
    return [(minLat + maxLat) / 2, (minLng + maxLng) / 2];
  };


  const portsByVessel = useMemo(() => {
    if (!combinedPortData || combinedPortData.length === 0) return {};

    return combinedPortData.reduce((acc, port) => {
      const indexName = port.indexName;
      if (!acc[indexName]) {
        acc[indexName] = [];
      }
      acc[indexName].push(port);
      return acc;
    }, {});
  }, [combinedPortData]);

  const centerPosition = getCenterPosition();

  const toggleVesselIndexSelection = (val) => {
    setSelectedOptions((prevSelected) => {
      let newSelection;
      if (prevSelected.includes(val)) {
        newSelection = prevSelected.filter((item) => item !== val);
      } else {
        if (prevSelected.length >= 10) {
          return prevSelected;
        }
        newSelection = [...prevSelected, val];
      }

      return newSelection;
    });
  };


  const filteredVesselIndexes = () => {
    return vesselIndexDetails.filter((val) =>
      val.indexName?.toLowerCase().includes(searchTerm?.toLowerCase())
    );
  };
  const groupedPorts = {};

  Object.entries(portsByVessel).forEach(([indexName, ports]) => {
    ports.forEach(port => {
      const key = `${port.lat},${port.lng}`;
      if (!groupedPorts[key]) {
        groupedPorts[key] = [];
      }
      groupedPorts[key].push({ ...port, vesselIndex: indexName });
    });
  });

  const getVesselColor = (indexName) => {
    const colors = ['red', 'blue', 'green', 'purple', 'orange', 'darkblue', 'darkred', 'darkgreen', 'teal', 'brown'];
    let hash = 0;
    for (let i = 0; i < indexName.length; i++) {
      hash = (hash << 5) - hash + indexName.charCodeAt(i);
      hash |= 0;
    }
    return colors[Math.abs(hash) % colors.length];
  };


  if (loading) return <div className="flex justify-center items-center h-screen">Loading map data...</div>;
  if (error) return <div className="flex justify-center items-center h-screen text-red-500">{error}</div>;
  return (
    <div className="flex h-full w-full relative">
      {/* Sidebar */}
      {
        !vesselIndexs && (
          <div
            className={`relative -left-6 -top-6 transition-all duration-300 ${hideSideBar ? "w-0 overflow-visible border-0" : "w-[280px]"
              } bg-[#eef2f6] rounded-xl rounded-l-none border border-gray-400 shadow-lg`}
          >
            <button
              onClick={() => setHideSidebar((prev) => !prev)}
              className="absolute right-[-2.54rem] top-32 p-3 bg-[#eef2f6] text-[#4052b6] rounded-full rounded-l-none border-l-0 transition z-[999] border border-gray-400"
            >
              {hideSideBar ? <FaArrowRight /> : <FaArrowLeft />}
            </button>
            <div className={`p-2 ${hideSideBar ? "hidden" : "block"}`}>
              <h2 className="text-center font-semibold text-sm text-gray-800 mb-4">
                Select Vessel indexes ({selectedOptions.length}/10 selected)
              </h2>
              {selectedOptions.length >= 10 && (
                <p className="text-rose-500 text-xs text-center mb-2">
                  Maximum selection limit reached (10)
                </p>
              )}
              <div className="mb-4">
                <input
                  type="text"
                  className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-500"
                  placeholder="Search vessel index..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div
                className="mt-4 flex flex-col gap-3 h-[calc(100vh-170px)] overflow-y-auto"
                style={{ scrollbarWidth: "thin" }}
              >
                {vesselIndexDetails.length > 0 ? (
                  filteredVesselIndexes?.().map((val, index) => {

                    return (
                      <div
                        key={index}
                        className={`flex gap-2 items-center ${(!val.hasValidPorts && !selectedOptions.includes(val.indexName)) ||
                          (selectedOptions.length >= 10 && !selectedOptions.includes(val.indexName))
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                          }`}
                        onClick={() => {
                          const isSelectable = val.hasValidPorts || selectedOptions.includes(val.indexName);
                          if (isSelectable) {
                            toggleVesselIndexSelection(val.indexName);
                          }
                        }}
                      >

                        <label
                          htmlFor={`vesselIndex-${val?._id}`}
                          className={`w-full flex items-center border ${selectedOptions.includes(val?.indexName)
                            ? "border-gray-600 bg-[#e6f7ff] hover:bg-[#dfe0f8]"
                            : "border-0"
                            } justify-between px-2 py-2 text-xs font-medium text-gray-900 bg-[#e6e8ec] rounded-md transition-all duration-300 transform hover:bg-[#cdcecf]`}
                        >
                          <span>{val?.indexName}</span>
                          {selectedOptions.includes(val?.indexName) && (
                            <span
                              className="ml-2 text-rose-500 hover:text-rose-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleVesselIndexSelection(val?.indexName);
                              }}
                            >
                              X
                            </span>
                          )}
                        </label>
                      </div>
                    );
                  })
                )
                  : (
                    <div className="text-center text-gray-700">
                      No Vessel Indexes found.
                    </div>
                  )}
              </div>
            </div>
          </div>
        )
      }
      {/* Map Container */}
      <div className="h-[60vh] w-full">
        <MapContainer
          center={centerPosition}
          zoom={2}
          style={{ height: '100%', width: '100%', zIndex: 1 }}
        >
          <MapViewController bounds={getMapBounds()} center={centerPosition} />
          <LayersControl position="topright">
            {/* Base Layers */}
            <LayersControl.BaseLayer name={tileLayers.worldImagery.name}>
              <TileLayer
                attribution={tileLayers.worldImagery.attribution}
                url={tileLayers.worldImagery.url}
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer checked name={tileLayers.worldStreet.name}>
              <TileLayer
                attribution={tileLayers.worldStreet.attribution}
                url={tileLayers.worldStreet.url}
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name={tileLayers.osm.name}>
              <TileLayer
                attribution={tileLayers.osm.attribution}
                url={tileLayers.osm.url}
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name={tileLayers.worldGrayCanvas.name}>
              <TileLayer
                attribution={tileLayers.worldGrayCanvas.attribution}
                url={tileLayers.worldGrayCanvas.url}
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name={tileLayers.blackAndWhite.name}>
              <TileLayer
                attribution={tileLayers.blackAndWhite.attribution}
                url={tileLayers.blackAndWhite.url}
              />
            </LayersControl.BaseLayer>

            {/* Overlay Layers */}
            <LayersControl.Overlay checked name="Sea Marks">
              <TileLayer
                attribution={tileLayers.seaMark.attribution}
                url={tileLayers.seaMark.url}
              />
            </LayersControl.Overlay>
            // In the LayersControl.Overlay section, replace the route coordinate logic with this:

            {Object.entries(portsByVessel).map(([indexName, ports]) => {
              const vesselColor = getVesselColor(indexName);
              const vesselData = vesselIndexDetails.find(v => v.indexName === indexName);

              let routeCoordinates = [];

              // First, try to use geojsonData if valid
              if (
                vesselData?.geojsonData &&
                Array.isArray(vesselData.geojsonData.coordinates) &&
                vesselData.geojsonData.coordinates.length >= 2
              ) {
                routeCoordinates = vesselData.geojsonData.coordinates
                  .map(coord => {
                    if (Array.isArray(coord) && coord.length >= 2) {
                      const [lng, lat] = coord;
                      return [lat, lng]; // Swap for Leaflet
                    }
                    return null;
                  })
                  .filter(Boolean);
              }

              // If geojsonData is invalid or insufficient, create route through all ports in sequence
              if (routeCoordinates.length < 2 && ports.length >= 2) {
                // Sort ports by their order if available, or use original order
                const sortedPorts = [...ports]; // You might want to sort by date, sequence, or other criteria
                routeCoordinates = sortedPorts.map(port => [port.lat, port.lng]);
              }

              // Unwrap across the antimeridian so the route stays continuous, then repeat it on every world copy
              const routeLines = unwrapLines([routeCoordinates.map(([lat, lng]) => [lng, lat])]);
              routeCoordinates = (routeLines[0] || []).map(([lng, lat]) => [lat, lng]);
              const routeLngs = [
                ...routeCoordinates.map(([, lng]) => lng),
                ...ports.map(port => alignLng(port.lng, routeLines)),
              ];

              return (
                <LayersControl.Overlay key={indexName} checked name={`${indexName} Route`}>
                  <LayerGroup>
                  <WorldCopies lngs={routeLngs}>
                  {(offset) => (<>
                    {/* Draw the main route line */}
                    {routeCoordinates.length >= 2 && (
                      <Polyline
                        positions={routeCoordinates.map(([lat, lng]) => [lat, lng + offset])}
                        color={vesselColor}
                        weight={3}
                        opacity={0.7}
                        smoothFactor={1.0}
                        noClip={true}
                      />
                    )}

                    {/* Optional: Draw individual port-to-port segments with different styling */}
                    {routeCoordinates.length < 2 && ports.length >= 2 && (
                      ports.slice(0, -1).map((port, segmentIndex) => (
                        <Polyline
                          key={`segment-${segmentIndex}`}
                          positions={[
                            [port.lat, alignLng(port.lng, routeLines) + offset],
                            [ports[segmentIndex + 1].lat, alignLng(ports[segmentIndex + 1].lng, routeLines) + offset]
                          ]}
                          color={vesselColor}
                          weight={3}
                          opacity={0.7}
                          dashArray="10, 5" // Dashed line to indicate it's a simplified route
                        />
                      ))
                    )}

                    {/* Place markers for each port */}
                    {Object.entries(groupedPorts).map(([key, ports]) => {
                      const [lat, lng] = key.split(',').map(Number);
                      const portsForThisVessel = ports.filter(port => port.vesselIndex === indexName);

                      if (portsForThisVessel.length > 0) {
                        return (
                          <Marker
                            key={`${indexName}-${key}`}
                            position={[lat, alignLng(lng, routeLines) + offset]}
                            icon={createCustomIcon(
                              ports.map(port => port.vesselIndex),
                              vesselColor,
                              indexName
                            )}
                          >
                            <Popup className='p-0 bottom-9'>
                              <div className="rounded-lg w-auto">
                                <h3 className="font-bold text-lg text-orange-600 mb-2">Information</h3>
                                {ports.map((port, i) => (
                                  <div key={i} className="text-sm border-t pt-2 mt-2 first:border-0 first:pt-0 first:mt-0">
                                    <p className="flex justify-between text-xs"><span className="font-semibold">Index Name:</span> {port.vesselIndex}</p>
                                    <p className="flex justify-between text-xs"><span className="font-semibold">Port:</span> {port.name || `Port ${i + 1}`}</p>
                                    <p className="flex justify-between text-xs"><span className="font-semibold">Country:</span> {port.country}</p>
                                    <p className="flex justify-between text-xs"><span className="font-semibold">Area:</span> {port.area}</p>
                                    <p className="flex justify-between text-xs"><span className="font-semibold">Coordinates:</span> {lat}, {lng}</p>
                                    <p className="flex justify-between text-xs"><span className="font-semibold">Port ID:</span> {port.portIdentifier}</p>
                                    <p className="flex justify-between text-xs"><span className="font-semibold">Time Zone:</span> UTC{port.timeZoneNormal >= 0 ? '+' : ''}{port.timeZoneNormal}</p>
                                  </div>
                                ))}
                              </div>
                            </Popup>
                          </Marker>
                        );
                      }
                      return null;
                    }).filter(Boolean)}
                  </>)}
                  </WorldCopies>
                  </LayerGroup>
                </LayersControl.Overlay>
              );
            })}
          <ZoneOverlays />
          </LayersControl>
        </MapContainer>
      </div>

    </div >
  );
};

export default VesselRouteMap;